/**
 * Fiche joueur en PAGE dédiée (overlay « plateau TV » qui monte, comme le détail
 * de match) : ouverte au CLIC/TAP d'un élément `[data-player]` (classements,
 * équipe type, mini-terrain). Réutilise l'archive — header (photo/poste/âge/nat),
 * stats du TOURNOI, courbe de notes match par match, et la liste détaillée.
 * Le survol garde l'aperçu rapide (playerStatsTip) ; le clic ouvre la fiche.
 */
import {
  fetchPlayer,
  fetchPlayerHistory,
  type PlayerInfo,
  type PlayerMatchRow,
} from "../app/playerData.ts";
import { schedule } from "../app/data.ts";
import { computePlayerMetrics } from "../app/metrics.ts";
import { fetchLeaderboards, type PositionBenchmark } from "../app/leaderboards.ts";
import { esc, flagCode, teamName, roundName, frontZ } from "./html.ts";
import { hidePlayerTip } from "./playerStatsTip.ts";
import { track } from "../app/track.ts";
import { t } from "../app/i18n.ts";

type PositionBenchmarks = Readonly<Record<string, PositionBenchmark>>;
// Repères « moyenne du poste » (leaderboards.json) : chargés UNE fois, en cache.
let benchmarksP: Promise<PositionBenchmarks | null> | null = null;
const loadBenchmarks = (): Promise<PositionBenchmarks | null> =>
  (benchmarksP ??= fetchLeaderboards().then((lb) => lb?.benchmarks ?? null));

const POS_KEY: Record<string, string> = {
  Goalkeeper: "fiche.player.pos.goalkeeper",
  Defender: "fiche.player.pos.defender",
  Midfielder: "fiche.player.pos.midfielder",
  Attacker: "fiche.player.pos.attacker",
};
/** Traduit un poste brut (« Goalkeeper ») au RENDU ; repli sur le brut si inconnu. */
const tPos = (p: string): string => {
  const k = POS_KEY[p];
  return k ? t(k) : p;
};
const rateCls = (r: number): string => (r >= 7.5 ? "hi" : r >= 6.5 ? "mid" : "lo");

// Calendrier indexé : matchId -> contexte (adversaire/date/tour).
const byMatch = new Map(schedule.map((m) => [m.id, m]));
const fmtDate = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
};

function oppOf(row: PlayerMatchRow): { code: string | null; label: string; date: string } {
  const m = byMatch.get(row.matchId);
  // L'adversaire vient du serveur (résolu, fiable même en phase finale) ; le
  // calendrier ne sert qu'au tour + à la date (ses home/away sont des
  // placeholders « 2A »… pour les matchs à élimination directe).
  const scheduleOpp = m ? (row.teamCode === m.home ? m.away : m.home) : null;
  const code = row.oppCode ?? scheduleOpp ?? null;
  if (!m) return { code, label: `${t("stats.col.match")} ${row.matchId}`, date: "" };
  const stage = m.stage === "group" ? `${t("stats.col.group")} ${m.group} · ${t("stats.col.played.abbr")}${m.matchday}` : roundName(m.stage);
  return { code, label: stage, date: fmtDate(m.utcDateTime) };
}

/** Petit graphe de l'évolution des notes (SVG pur, série unique). */
function ratingCurve(matches: readonly PlayerMatchRow[]): string {
  const pts = matches
    .map((m, i) => ({ i, opp: oppOf(m).code, r: m.rating }))
    .filter((p): p is { i: number; opp: string | null; r: number } => p.r != null);
  if (pts.length === 0) return "";
  const W = 300;
  const H = 132;
  const pad = { l: 10, r: 10, t: 20, b: 26 };
  const rs = pts.map((p) => p.r);
  const lo = Math.max(3, Math.floor(Math.min(...rs) - 0.4));
  const hi = Math.min(10, Math.ceil(Math.max(...rs) + 0.4));
  const span = Math.max(0.5, hi - lo);
  const n = pts.length;
  const sx = (i: number): number => (n === 1 ? W / 2 : pad.l + (i / (n - 1)) * (W - pad.l - pad.r));
  const sy = (r: number): number => pad.t + ((hi - r) / span) * (H - pad.t - pad.b);
  const xy = pts.map((p, k) => ({ ...p, x: sx(k), y: sy(p.r) }));
  const line = xy.map((p, k) => `${k === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const avg = rs.reduce((a, b) => a + b, 0) / rs.length;
  const avgY = sy(avg).toFixed(1);
  const dots = xy
    .map(
      (p) =>
        `<circle class="pp-cv-dot ${rateCls(p.r)}" cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="4"/>` +
        `<text class="pp-cv-val" x="${p.x.toFixed(1)}" y="${(p.y - 9).toFixed(1)}" text-anchor="middle">${p.r.toFixed(1)}</text>` +
        `<text class="pp-cv-x" x="${p.x.toFixed(1)}" y="${H - 8}" text-anchor="middle">${esc(p.opp ?? "")}</text>`,
    )
    .join("");
  return `<div class="pp-block-h">${t("fiche.player.ratingCurve.title")}</div>
    <svg viewBox="0 0 ${W} ${H}" class="pp-curve" role="img" aria-label="${t("fiche.player.ratingCurve.ariaLabel")}">
      <line class="pp-cv-avg" x1="${pad.l}" y1="${avgY}" x2="${W - pad.r}" y2="${avgY}"/>
      <text class="pp-cv-avgt" x="${W - pad.r}" y="${(Number(avgY) - 4).toFixed(1)}" text-anchor="end">${t("common.avgAbbr")} ${avg.toFixed(2)}</text>
      ${n > 1 ? `<path class="pp-cv-line" d="${line}"/>` : ""}
      ${dots}
    </svg>`;
}

/** Bande de stats fines du match (tirs, passes clés, duels, dribbles, tacles) :
 *  uniquement les valeurs présentes et non nulles — les données du flux par match
 *  (déjà servies par /data/player-history) sont ainsi exploitées, pas seulement note/buts. */
function matchStats(m: PlayerMatchRow): string {
  const chip = (val: string, label: string): string =>
    `<span class="pp-m-st"><b>${val}</b> ${label}</span>`;
  const chips: string[] = [];
  if ((m.shots ?? 0) > 0) chips.push(chip(String(m.shots), m.shotsOn != null ? `${t("stats.pill.shots")} · ${m.shotsOn} ${t("stats.shotsOnAbbr")}` : t("stats.pill.shots")));
  if ((m.keyPasses ?? 0) > 0) chips.push(chip(String(m.keyPasses), t("common.keyPasses")));
  if ((m.passes ?? 0) > 0) chips.push(chip(String(m.passes), m.passAcc != null ? `${t("common.assists")} · ${Math.round(m.passAcc)}%` : t("common.assists")));
  if ((m.duelsTotal ?? 0) > 0) chips.push(chip(`${m.duelsWon ?? 0}/${m.duelsTotal}`, t("common.duels")));
  if ((m.dribblesAtt ?? 0) > 0) chips.push(chip(`${m.dribbles ?? 0}/${m.dribblesAtt}`, t("common.dribbles")));
  if ((m.tackles ?? 0) > 0) chips.push(chip(String(m.tackles), t("common.tackles")));
  return chips.length ? `<div class="pp-m-stats">${chips.join("")}</div>` : "";
}

function matchRow(m: PlayerMatchRow): string {
  const { code, label, date } = oppOf(m);
  const flag = code ? `<span class="f fi fi-${esc(flagCode(code))}"></span><span class="pp-m-opp">${esc(code)}</span>` : "";
  const rate = m.rating != null ? `<span class="pp-rate ${rateCls(m.rating)}">${m.rating.toFixed(1)}</span>` : `<span class="pp-rate dim">—</span>`;
  const ga = [
    m.goals ? `<span class="pp-ga" title="${t("common.goals")}"><i class="ti ti-ball-football" aria-hidden="true"></i>${m.goals}</span>` : "",
    m.assists ? `<span class="pp-ga" title="${t("common.assists.v2")}"><i class="ti ti-shoe" aria-hidden="true"></i>${m.assists}</span>` : "",
  ].join("");
  const min = m.minutes != null ? `<span class="pp-min">${m.minutes}′</span>` : "";
  return `<li class="pp-m">
    <span class="pp-m-team">${flag}</span>
    <span class="pp-m-ctx"><span class="pp-m-stage">${esc(label)}</span><span class="pp-m-date">${esc(date)}</span></span>
    <span class="pp-m-ga">${ga}${min}</span>
    ${rate}
    ${matchStats(m)}
  </li>`;
}

function profileHtml(
  name: string,
  info: PlayerInfo | null,
  matches: readonly PlayerMatchRow[],
  bench: PositionBenchmarks | null,
): string {
  const photo = info?.photo
    ? `<img class="pp-photo" src="${esc(info.photo)}" alt="" loading="lazy" referrerpolicy="no-referrer">`
    : `<div class="pp-photo ph"><i class="ti ti-user" aria-hidden="true"></i></div>`;
  const team = matches.find((m) => m.teamCode)?.teamCode ?? null;
  const pos = info?.pos ? tPos(info.pos) : null;
  const sub = [pos, info?.age != null ? `${info.age} ${t("common.yearsOld")}` : null, info?.nationality].filter(Boolean).join(" · ");
  const teamLine = team
    ? `<div class="pp-team"><span class="f fi fi-${esc(flagCode(team))}"></span>${esc(teamName(team))}</div>`
    : "";

  // Agrégats du TOURNOI (depuis l'historique, pas la saison).
  const apps = matches.filter((m) => (m.minutes ?? 0) > 0).length || matches.length;
  const goals = matches.reduce((s, m) => s + (m.goals ?? 0), 0);
  const assists = matches.reduce((s, m) => s + (m.assists ?? 0), 0);
  const rated = matches.map((m) => m.rating).filter((r): r is number => r != null);
  const avg = rated.length ? rated.reduce((a, b) => a + b, 0) / rated.length : null;
  const tile = (v: string, l: string, cls = "") => `<div class="pp-tile"><span class="pp-tv ${cls}">${v}</span><span class="pp-tl">${l}</span></div>`;

  const stats = `<div class="pp-stats">
    ${tile(String(apps), t("fiche.player.apps"))}
    ${tile(String(goals), t("common.goals"))}
    ${tile(String(assists), t("common.assists"))}
    ${tile(avg != null ? `<span class="pp-rate ${rateCls(avg)}">${avg.toFixed(2)}</span>` : "—", t("fiche.player.avgRating"))}
  </div>`;

  const list = matches.length
    ? `<div class="pp-block-h">${t("fiche.player.matchByMatch")}</div><ul class="pp-matches">${matches.map(matchRow).join("")}</ul>`
    : `<div class="pp-empty">${t("fiche.player.noMatches")}</div>`;

  return `<div class="pp-head">
      ${photo}
      <div class="pp-id">
        <div class="pp-name">${esc(info?.name ?? name)}</div>
        ${sub ? `<div class="pp-sub">${esc(sub)}</div>` : ""}
        ${teamLine}
      </div>
    </div>
    ${stats}
    ${metricsHtml(matches, info?.pos ?? null, bench)}
    ${ratingCurve(matches)}
    ${list}`;
}

// Métriques mises en avant SELON LE POSTE (les plus pertinentes d'abord).
// Métriques de VOLUME (plus haut ≠ meilleur) → écart montré mais NON coloré bon/mauvais.
const NEUTRAL_METRICS = new Set<string>(["tacklesPer90"]);
const METRIC_ORDER: Record<string, readonly string[]> = {
  Attacker: ["conversion", "gaPer90", "dribblePct", "keyPassesPer90", "duelWinPct", "passAcc", "tacklesPer90"],
  Midfielder: ["keyPassesPer90", "passAcc", "gaPer90", "dribblePct", "duelWinPct", "tacklesPer90", "conversion"],
  Defender: ["duelWinPct", "tacklesPer90", "passAcc", "keyPassesPer90", "dribblePct", "gaPer90", "conversion"],
  Goalkeeper: ["passAcc", "duelWinPct", "tacklesPer90"],
};

/** Métriques dérivées (efficacité / créativité / duels…), cumulé tournoi, ORDONNÉES selon
 *  le poste (un défenseur voit duels/tacles en tête, un attaquant la conversion/buts), et
 *  SITUÉES vs la moyenne du poste sur le tournoi (« +18 % vs moy. ») quand le repère existe. */
function metricsHtml(matches: readonly PlayerMatchRow[], pos: string | null, bench: PositionBenchmarks | null): string {
  const m = computePlayerMetrics(matches);
  if (!m) return "";
  const pct = (v: number | null): string | null => (v == null ? null : `${Math.round(v * 100)}%`);
  const dec = (v: number | null, d = 1): string | null => (v == null ? null : v.toFixed(d));
  const def: Record<string, { l: string; raw: number | null; v: string | null }> = {
    conversion: { l: t("stats.metric.conversion"), raw: m.conversion, v: pct(m.conversion) },
    gaPer90: { l: t("stats.metric.gaPer90"), raw: m.gaPer90, v: dec(m.gaPer90, 2) },
    keyPassesPer90: { l: t("stats.metric.keyPassesPer90"), raw: m.keyPassesPer90, v: dec(m.keyPassesPer90) },
    passAcc: { l: t("stats.metric.passAcc"), raw: m.passAcc, v: m.passAcc != null ? `${Math.round(m.passAcc)}%` : null },
    duelWinPct: { l: t("stats.metric.duelWinPct"), raw: m.duelWinPct, v: pct(m.duelWinPct) },
    dribblePct: { l: t("stats.metric.dribblePct"), raw: m.dribblePct, v: pct(m.dribblePct) },
    tacklesPer90: { l: t("stats.metric.tacklesPer90"), raw: m.tacklesPer90, v: dec(m.tacklesPer90) },
  };
  const order = METRIC_ORDER[pos ?? ""] ?? Object.keys(def);
  const posBench = pos && bench ? (bench[pos] as Record<string, number | null> | undefined) : undefined;
  const cells = order
    .map((k) => {
      const it = def[k];
      if (!it || it.v == null) return null;
      const avg = posBench?.[k];
      let delta = "";
      if (avg != null && avg > 0 && it.raw != null) {
        const d = Math.round((it.raw / avg - 1) * 100); // % au-dessus/en-dessous de la moyenne du poste
        // Tacles/90 = un VOLUME (plus ≠ mieux) → on montre l'écart sans le colorer en bon/mauvais.
        const cls = NEUTRAL_METRICS.has(k) ? "" : d >= 5 ? "up" : d <= -5 ? "down" : "";
        delta = `<span class="pp-met-d ${cls}" title="${esc(t("fiche.player.vsPosAvg", { pos: pos ? tPos(pos).toLowerCase() : "" }))}">${d > 0 ? "+" : ""}${d}%</span>`;
      }
      return `<div class="pp-met"><span class="pp-met-v">${esc(it.v)}</span><span class="pp-met-l">${esc(it.l)}</span>${delta}</div>`;
    })
    .filter((x): x is string => x !== null);
  if (!cells.length) return "";
  const posLabel = pos ? tPos(pos) : null;
  const hint = posBench ? ` · ${t("fiche.player.metrics.vsPosAvg")}` : "";
  return `<div class="pp-block-h">${t("fiche.player.metrics.title")} <span class="count">${t("fiche.player.metrics.cumTournament")}${posLabel ? ` · ${t("fiche.player.metrics.profile", { pos: esc(posLabel.toLowerCase()) })}` : ""}${hint}</span></div>
    <div class="pp-metrics">${cells.join("")}</div>`;
}

// --- contrôleur overlay (réutilise la coquille .md-overlay) -----------------
let overlay: HTMLElement | null = null;
let open = false;
let installed = false;
let token = 0;

function ensureOverlay(): HTMLElement {
  if (overlay) return overlay;
  const ov = document.createElement("div");
  ov.className = "md-overlay pp-overlay hide";
  ov.innerHTML = `<div class="md-card pp-card" role="dialog" aria-modal="true" aria-label="${t("fiche.player.dialogLabel")}"><button type="button" class="md-close" aria-label="${t("fiche.closeProfile")}"><i class="ti ti-x" aria-hidden="true"></i></button><div class="md-body pp-body"></div></div>`;
  ov.addEventListener("click", (e) => {
    if (e.target === ov || (e.target instanceof Element && e.target.closest(".md-close"))) close();
  });
  ov.querySelector(".md-close")?.addEventListener("click", () => close());
  overlay = ov;
  document.body.appendChild(ov);
  return ov;
}

async function openProfile(id: number, name: string): Promise<void> {
  hidePlayerTip();
  track("fiche", { kind: "player", id });
  const ov = ensureOverlay();
  const body = ov.querySelector<HTMLElement>(".pp-body");
  if (!body) return;
  const mine = ++token;
  // squelette immédiat (ressenti instantané) puis on remplit dès les données prêtes
  body.innerHTML = `<div class="pp-head"><div class="pp-photo ph"><i class="ti ti-user" aria-hidden="true"></i></div><div class="pp-id"><div class="pp-name">${esc(name)}</div><div class="pp-sub">${t("common.loading")}</div></div></div>`;
  ov.classList.remove("hide");
  ov.style.zIndex = String(frontZ()); // au-dessus de toute modale déjà ouverte
  open = true;
  requestAnimationFrame(() => ov.classList.add("open"));
  const [info, hist, bench] = await Promise.all([fetchPlayer(id), fetchPlayerHistory(id), loadBenchmarks()]);
  if (mine !== token || !open) return; // une autre fiche a été ouverte / fermée entre-temps
  body.innerHTML = profileHtml(name, info, hist?.matches ?? [], bench);
}

function close(): void {
  if (!overlay || !open) return;
  open = false;
  token++;
  overlay.classList.remove("open");
  const ov = overlay;
  setTimeout(() => {
    if (!open) ov.classList.add("hide");
  }, 300);
}

const playerEl = (e: Event): HTMLElement | null =>
  e.target instanceof Element ? e.target.closest<HTMLElement>("[data-player]") : null;

function openFromEl(el: HTMLElement): void {
  const id = Number(el.dataset.player);
  if (!Number.isInteger(id)) return;
  void openProfile(id, el.dataset.name ?? t("fiche.player.fallbackName"));
}

/** Installe le contrôleur (idempotent) : clic/tap/clavier sur un joueur → fiche. */
export function registerPlayerProfile(): void {
  if (installed) return;
  installed = true;
  document.addEventListener("click", (e) => {
    const el = playerEl(e);
    if (!el) return;
    e.preventDefault();
    openFromEl(el);
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      close();
      return;
    }
    // Enter / Espace sur un joueur focalisé (tabindex=0) → ouvre la fiche (WCAG 2.1.1) :
    // ces éléments annoncent leur interactivité (curseur, focus visible) sans être des <button>.
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
      const el = e.target instanceof Element ? e.target.closest<HTMLElement>("[data-player]") : null;
      if (el) {
        e.preventDefault();
        openFromEl(el);
      }
    }
  });
}
